import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { api, imageUrl, formatApiError } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { ProductImagePlaceholder } from "@/components/site/Logo";
import { Reveal } from "@/components/site/Reveal";
import { toast } from "sonner";

const initial = { name: "", email: "", address_line1: "", address_line2: "", city: "", state: "", postal_code: "", country: "US" };

export default function Checkout() {
  const { cart, cartSubtotal, settings } = useShop();
  const [form, setForm] = useState(initial);
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [placing, setPlacing] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const shippingFee = shippingMethod === "express" ? settings?.shipping_express ?? 15 : settings?.shipping_flat ?? 10.5;
  const total = useMemo(() => cartSubtotal + shippingFee, [cartSubtotal, shippingFee]);

  if (cart.length === 0 && !placing) {
    navigate("/cart");
    return null;
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = "Valid email required";
    if (!form.address_line1.trim()) errs.address_line1 = "Required";
    if (!form.city.trim()) errs.city = "Required";
    if (!form.postal_code.trim()) errs.postal_code = "Required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setPlacing(true);
    try {
      const { data } = await api.post("/checkout", {
        items: cart.map((i) => ({ product_id: i.product_id, size_label: i.size_label, quantity: i.quantity })),
        customer: form,
        shipping_method: shippingMethod,
        origin_url: window.location.origin,
        idempotency_key: crypto.randomUUID(),
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setPlacing(false);
    }
  };

  const field = (key, label, opts = {}) => (
    <div key={key}>
      <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] block mb-2">{label}</label>
      <input
        {...opts}
        value={form[key]}
        onChange={set(key)}
        className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3.5 font-sans-body text-sm text-white placeholder:text-[#666666] focus:outline-none focus:border-[#D4AF37] transition-colors"
        data-testid={`checkout-${key}-input`}
      />
      {errors[key] && <p className="font-sans-body text-xs text-red-400 mt-1">{errors[key]}</p>}
    </div>
  );

  return (
    <div className="pt-32 pb-24 max-w-6xl mx-auto px-4 sm:px-8 lg:px-16" data-testid="checkout-page">
      <Reveal>
        <p className="eyebrow mb-3">Secure Checkout</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-white font-light">Checkout</h1>
        <p className="font-sans-body text-xs text-[#B8B8B8] font-light mt-4 flex items-center gap-2">
          <ShieldCheck size={15} className="text-[#D4AF37]" />
          Payment is processed securely by Stripe. We never see or store your card details.
        </p>
      </Reveal>

      <form onSubmit={placeOrder} className="grid grid-cols-1 lg:grid-cols-3 gap-12 mt-12">
        <div className="lg:col-span-2 space-y-8">
          <div className="gold-hairline bg-[#0D0D0D] p-8 space-y-5">
            <h2 className="font-serif-display text-2xl text-white">Contact &amp; Shipping</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {field("name", "Full Name")}
              {field("email", "Email Address", { type: "email" })}
            </div>
            {field("address_line1", "Address Line 1")}
            {field("address_line2", "Address Line 2 (optional)")}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {field("city", "City")}
              {field("state", "State / Region")}
              {field("postal_code", "Postal Code")}
            </div>
            <div>
              <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] block mb-2">Country</label>
              <select value={form.country} onChange={set("country")} data-testid="checkout-country-select"
                className="w-full bg-[#0D0D0D] border border-[#D4AF37]/25 px-4 py-3.5 font-sans-body text-sm text-white focus:outline-none focus:border-[#D4AF37]">
                {["US", "CA", "GB", "AU", "DE", "FR", "NL", "IE", "ES", "IT", "BE", "AT", "SE", "NO", "DK", "FI", "CH", "JP", "SG", "NZ"].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="gold-hairline bg-[#0D0D0D] p-8 space-y-4">
            <h2 className="font-serif-display text-2xl text-white">Shipping Method</h2>
            {[
              { key: "standard", label: "Standard Shipping", price: settings?.shipping_flat ?? 10.5 },
              { key: "express", label: "Express Shipping", price: settings?.shipping_express ?? 15 },
            ].map((m) => (
              <label key={m.key}
                className={`flex items-center justify-between border px-5 py-4 cursor-pointer transition-colors ${
                  shippingMethod === m.key ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#D4AF37]/20 hover:border-[#D4AF37]/50"
                }`}
                data-testid={`shipping-${m.key}`}>
                <span className="flex items-center gap-3">
                  <input type="radio" name="shipping" checked={shippingMethod === m.key} onChange={() => setShippingMethod(m.key)} className="accent-[#D4AF37]" />
                  <span className="font-sans-body text-sm text-white">{m.label}</span>
                </span>
                <span className="font-mono-price text-[#D4AF37]">${m.price.toFixed(2)}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="gold-hairline bg-[#0D0D0D] p-8 h-fit space-y-5" data-testid="checkout-summary">
          <h2 className="font-serif-display text-2xl text-white">Order Summary</h2>
          <div className="space-y-4 max-h-64 overflow-y-auto pr-1">
            {cart.map((item) => (
              <div key={`${item.product_id}-${item.size_label}`} className="flex gap-4 items-center">
                <div className="w-12 h-16 shrink-0 overflow-hidden gold-hairline relative">
                  {item.image_url ? (
                    <img src={imageUrl(item.image_url)} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <ProductImagePlaceholder name={item.name} className="w-full h-full" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-sans-body text-sm text-white truncate">{item.name}</p>
                  <p className="font-sans-body text-[10px] uppercase tracking-[0.15em] text-[#C6A15B]">{item.size_label} × {item.quantity}</p>
                </div>
                <span className="font-mono-price text-sm text-white">${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="gold-hairline-t pt-5 space-y-3">
            <div className="flex justify-between font-sans-body text-sm text-[#B8B8B8]">
              <span>Subtotal</span><span className="font-mono-price text-white" data-testid="checkout-subtotal">${cartSubtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-sans-body text-sm text-[#B8B8B8]">
              <span>Shipping ({shippingMethod})</span><span className="font-mono-price text-white">${shippingFee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-sans-body text-sm text-[#B8B8B8]">
              <span>Tax</span><span className="font-sans-body text-xs text-[#666666]">Calculated at payment</span>
            </div>
            <div className="flex justify-between gold-hairline-t pt-4">
              <span className="font-sans-body text-sm uppercase tracking-[0.2em] text-white">Total</span>
              <span className="font-mono-price text-xl text-[#D4AF37]" data-testid="checkout-total">${total.toFixed(2)}</span>
            </div>
          </div>
          <button type="submit" className="btn-gold w-full" disabled={placing} data-testid="checkout-pay-button">
            {placing ? "Redirecting to secure payment..." : "Continue to Secure Payment"}
          </button>
          <p className="font-sans-body text-[11px] text-[#B8B8B8] font-light text-center" data-testid="payment-methods-note">
            Cards, Apple Pay and Google Pay accepted — Apple Pay appears automatically on supported Apple devices.
          </p>
          <p className="font-sans-body text-[11px] text-[#666666] font-light text-center">
            By placing this order you agree to our <a href="/policies/terms" className="underline underline-offset-2">terms</a>.
          </p>
        </div>
      </form>
    </div>
  );
}
